export type Language = 'en' | 'es'

// ─── English ──────────────────────────────────────────────────────────────────
const en = {
  // Shared header / nav
  portalName: 'Goodiez Portal',
  companyName: 'Home',
  signOut: 'Sign out',
  backToDashboard: '← Dashboard',
  adminOnly: 'ADMIN ONLY',
  comingSoon: 'COMING SOON',

  // Theme switcher labels (used in ThemeSwitcher.tsx)
  dark: 'Dark',
  light: 'Light',

  // ── Login ──────────────────────────────────────────────────────────────────
  login: {
    title: 'Goodiez Portal',
    subtitle: 'Home · Authorized Personnel Only',
    signInGoogle: 'Sign in with Google',
    restricted: 'Access restricted to authorized @greenlabsllc.com accounts only.',
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  dashboard: {
    welcome: (name: string) => `Welcome back, ${name} 👋`,
    adminAccess: 'Full administrative access.',
    staffAccess: 'Staff access.',
  },

  // ── Module tiles (dashboard) ──────────────────────────────────────────────
  modules: {
    recipes:    { title: 'Products',    desc: 'Manage products, recipes & procedures' },
    inventory:  { title: 'Inventory',  desc: 'Track ingredients & stock levels' },
    growflow:   { title: 'Product Inventory', desc: 'Live GrowFlow data — all rooms & SKUs' },
    production: { title: 'Production', desc: 'Batch tracking & scheduling' },
    employees:  { title: 'Employees',  desc: 'Staff & access management' },
    settings:    { title: 'Settings',    desc: 'System configuration' },
    maintenance: { title: 'Maintenance', desc: 'Equipment status & parts management' },
    sales:       { title: 'Sales',       desc: 'Quarterly data & sales intelligence' },
    accessControl: { title: 'Access Control', desc: 'Manage users, invitations & page permissions' },
  },

  // ── Employees ─────────────────────────────────────────────────────────────
  employees: {
    title: 'Employees',
    subtitle: 'Manage staff records, skills, and training — admin only',
    employeeListTab: 'Employee List',
    timeOffCalendarTab: 'Time-Off Calendar',
    roster: {
      title: 'Employee Roster',
      desc: 'View and manage all employees — open anyone to see their skills, training scores and checklists',
    },
    skillsMatrix: {
      title: 'Skills Matrix',
      desc: 'Track which skills each employee holds, who leads each area, and backup coverage status',
    },
    trainingInProgress: {
      title: 'Training In Progress',
      desc: 'Monitor active training records, mark completions, and assign new skills for development',
    },
    productionTeams: {
      title: 'Production Teams',
      desc: 'Read-only intelligence on team composition, coverage, batch feasibility, and health per product family',
    },
    skillEligibility: {
      title: 'Skill Eligibility',
      desc: 'View required creation skills and employee eligibility per recipe',
    },
  },

  // ── Products (was Recipes) ──────────────────────────────────────────────────
  recipes: {
    title: 'Products',
    subtitle: 'Manage products, recipes & procedures',
    newRecipe:            { title: 'New Batch',                   desc: 'Start a new production batch — select SKU, flavor, and servings with full cannabinoid dosing' },
    sops:                 { title: 'SOPs',                       desc: 'Standard operating procedures for production' },
    recipeBooks:          { title: 'Recipe Books',               desc: 'Browse all saved recipe collections' },
    recipeLeaflinkMapping:{ title: 'Recipe to LeafLink Mapping', desc: 'Align recipe flavor names with canonical LeafLink product names' },
    ingredientMapping:    { title: 'Ingredient Mapping',         desc: 'Resolve recipe ingredient names to canonical inventory records' },
    allSku:               { title: 'SKU Management',            desc: 'Manage all product SKUs — create, edit, and configure production items', deleteProduct: 'Delete Product', deleteConfirmMessage: 'Are you sure you want to permanently delete this product?', deleteNote: 'Recipes, inventory, and packaging items will NOT be affected.', cancelDelete: 'Cancel', confirmDelete: 'Delete Permanently', deletingProduct: 'Deleting…' },
  },

  // ── Inventory ─────────────────────────────────────────────────────────────
  inventory: {
    title: 'Inventory',
    subtitle: 'Track ingredients, packaging materials, and finished product stock levels',
    productInventory: {
      title: 'Product Inventory',
      desc: 'Finished goods inventory tracked in GrowFlow (WSLCB traceability system)',
    },
    growFlowInventory: {
      title: 'GrowFlow Inventory',
      desc: 'Live GrowFlow data — all rooms, all SKUs with LeafLink name resolution',
    },
    productNameMapping: {
      title: 'Product Name Mapping',
      desc: 'Manage product name mappings between GrowFlow and LeafLink naming conventions',
    },
    growFlowPlayland: {
      title: 'GrowFlow Playland',
      desc: 'Sandbox for exploring GrowFlow create/destroy flows — restricted to the Fabian Test Room',
    },
    expirationTracking: {
      title: 'Expiration Tracking',
      desc: 'Items past or nearing their SKU sell window, with sales-velocity signals for each at-risk batch',
    },
    packagingIngredients: {
      title: 'Packaging & Ingredients',
      desc: 'Track packaging materials and ingredients — daily delivery logging, reorder alerts, and full inventory counts',
    },
    howItWorks: 'How this works',
    workflowGuide: 'WORKFLOW GUIDE',
    splitExplain:
      'Inventory is split into two separate systems because they serve different purposes and are managed by different tools:',
    productInventoryExplain:
      'Finished gummies, truffles, edibles ready for sale. Tracked in GrowFlow for WSLCB traceability compliance. Any movement of finished product must go through GrowFlow.',
    packagingExplain:
      'Raw materials and packaging supplies used in production. Tracked here in this system. Updated daily on delivery and weekly during full inventory counts.',
    daily: 'DAILY',
    weekly: 'WEEKLY',
    dailySteps: [
      {
        icon: '🚚',
        title: 'Log every delivery',
        detail: 'Open Packaging & Ingredients → Delivery Log tab. Enter the carrier, tracking number, R/D type, contents (be specific — e.g. "50 lb sugar, 3 cases chocolate"), and who signed. Do this the moment a delivery arrives.',
      },
      {
        icon: '✏️',
        title: 'Update quantities after each delivery',
        detail: 'Go to the relevant tab (Ingredients, Packaging, Rolls, etc.). Click the Current column cell for each received item — it will become an editable field. Type the new total on hand and press Enter to save. Also update the Received and Last Rcvd columns.',
      },
      {
        icon: '🔴',
        title: 'Check reorder alerts',
        detail: 'On each tab, click the red "Reorder needed" filter button to see only items that need ordering. Items with a red stock bar or Reorder? = YES are the priority. Pass the filtered list to purchasing.',
      },
    ],
    weeklySteps: [
      {
        icon: '🧮',
        title: 'Full physical inventory count',
        detail: 'Walk through the facility and count everything. Update the Current column on each tab to reflect actual quantities on hand. Work through tabs in order: Ingredients → Packaging → Rolls → Dipped → Lab → General.',
      },
      {
        icon: '📋',
        title: 'Build purchase orders from reorder list',
        detail: 'Use the "Reorder needed" filter on Ingredients and Packaging tabs. Cross-reference the 3-Mo Min column (order immediately if below) and 6-Mo Supply column (ideal buffer). Packaging and Rolls tabs are forecast-driven — shortfalls are calculated automatically.',
      },
      {
        icon: '📦',
        title: 'Verify delivery log is complete',
        detail: 'Check the Delivery Log tab and confirm every delivery from the week is logged. Make sure Last Rcvd and Received columns on each inventory tab match what actually came in. Gaps here cause miscounts at the next full count.',
      },
    ],
  },

  // ── Maintenance ──────────────────────────────────────────────────────────
  maintenance: {
    title: 'Maintenance',
    subtitle: 'Equipment status monitoring and service records',
    status: {
      title: 'Now Maintenance',
      desc: 'Due protocols, machine photos, checklists, and scheduling for today',
    },
    equipment: {
      title: 'Equipment',
      desc: 'Manage machines, maintenance schedules, and service records',
    },
    machineParts: {
      title: 'Machine Parts',
      desc: 'Spare parts and consumables for every machine — stock, reorder flags, and part numbers',
    },
  },

  // ── Settings sub-modules ──────────────────────────────────────────────
  settingsSub: {
    bugs: {
      title: 'Bug Reports',
      desc: 'View, triage, and manage reported bugs and feature requests',
    },
    disasterPlanning: {
      title: 'Backups & Restore',
      desc: 'Durable backups, recovery drills, and safeguarded restore — Sprint 2 + Sprint 3 disaster planning, all in one place',
    },
    trash: {
      title: 'Trash',
      desc: 'Soft-deleted products, batches, and recipe books — restore in one click before resorting to a full backup restore',
    },
  },

  // ── Bug report modal ──────────────────────────────────────────────────
  bugReport: {
    modalTitle: 'Report a Bug',
  },

  // ── Sales ───────────────────────────────────────────────────────────────
  sales: {
    title: 'Sales',
    subtitle: 'Sales analytics, quarterly performance, and data intelligence',
    quarterlyData: {
      title: 'Quarterly Data',
      desc: 'View sales performance, revenue trends, and quarterly comparisons',
    },
    dataIntelligence: {
      title: 'Data Intelligence Center',
      desc: 'Market insights, forecasting, and advanced sales analytics',
    },
    returns: {
      title: 'Returns',
      desc: 'Track product returns from stores, manage resolutions, and view trends',
    },
    complaints: {
      title: 'Customer Complaints',
      desc: 'Every complaint as a case — the customer side and the facility side, and whose move it is on each',
      badge: '{n} open',
    },
    payments: {
      title: 'Payments',
      desc: 'The retail payment tracker — the Wednesday hand-off, each invoice\'s outcome, the outstanding list',
    },
    nonInfused: {
      title: 'Non-Infused Requests',
      desc: 'File and track non-cannabis sample requests for SKU/flavor demos',
    },
    training: {
      title: 'Training',
      desc: 'Portal training sessions — watch the team walkthrough recordings',
    },
  },

  // ── Production ────────────────────────────────────────────────────────────
  production: {
    title: 'Production',
    subtitle: 'Batch tracking, scheduling, and production calendar',
    productionDashboard: {
      title: 'Production Dashboard',
      desc: 'View and manage production batches, status filters, and batch details',
    },
    productionTv: {
      title: 'TV',
      desc: 'Wall-mounted TV view of the production floor — large fonts, room sensors, 3D batch cards, read-only',
    },
    calendar: {
      title: 'Calendar',
      desc: 'Visualize scheduled and completed batches on a production calendar',
    },
    currentSchedule: {
      title: 'Current Production Schedule',
      desc: 'Plan, commit, and manage real production schedule entries with simulation support and calendar view',
    },
    productionTriggers: {
      title: 'Production Triggers',
      desc: 'Manage automated triggers and alerts for production milestones and thresholds',
    },
    schedulingSettings: {
      title: 'Scheduling Settings',
      desc: 'Configure lead coverage rules and scheduling thresholds for production teams and auto-scheduling',
    },
    planningLab: {
      title: 'Planning Lab',
      desc: 'Simulate team scenarios, model staffing changes, and forecast quarterly production capacity',
    },
    productionStats: {
      title: 'Production Stats',
      desc: 'Scheduler debug view — compare SKUs by labor costs, efficiency, and production capacity',
    },
    complaints: {
      title: 'Complaints',
      desc: 'Customer complaints under investigation — identify the batch, investigate, and Blade writes the outcome',
      badge: '{n} under investigation',
    },
    pdfMerge: {
      title: 'Merge PDFs',
      desc: 'Combine print sheets, COAs or manifests into one PDF — in your browser, nothing is uploaded',
    },
    // 🧼 BUG-mvljf8uq-su6a — the paper "Master Sanitization Record", one week per page.
    sanitationLog: {
      title: 'Daily Sanitation Log',
      desc: 'Tick off and sign the room\u2019s daily sanitation — one week per sheet, kept for records and ready to print',
      weekOf: 'For the week of',
      itemEquipment: 'Item / Equipment',
      initials: 'Initials',
      mgr: 'Mgr',
      comments: 'Comments',
      commentPlaceholder: 'Comment for the week\u2026',
      notes: 'Notes',
      approvedBy: 'Approved by',
      signToday: "Sign today\u2019s column",
      print: 'Print',
      previousWeek: 'Previous week',
      nextWeek: 'Next week',
      thisWeek: 'This week',
      thisWeekSoFar: 'Signed so far',
      weekComplete: 'Week complete',
      records: 'Stored weeks',
      tapToSign: 'Tap to sign as the person who cleaned it',
      tapToCountersign: 'Tap to countersign as manager',
      notYet: 'That day has not happened yet',
      readOnly: 'You can read this sheet but not sign it \u2014 ask for Edit on Production in Access Control.',
    },
  },

  // ── Shared UI ─────────────────────────────────────────────────────────────
  shared: {
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    loading: 'Loading…',
    dismiss: 'dismiss',
    backToEmployees: '← Employees',
    backToRecipes: '← Recipes',
    backToProduction: '← Production',
    backToDashboardFull: 'Back to Dashboard',
    reset: 'Reset',
    noData: '—',
  },

  // ── Invite Accept ────────────────────────────────────────────────────────
  inviteAccept: {
    internalDataHub: 'Internal Data Hub',
    accepting: 'Accepting your invitation...',
    needSignIn: 'You need to sign in with Google before accepting this invite.',
    inviteFor: 'This invite is for:',
    signInWith: (email: string) => `Sign in with the Google account for`,
    thenComplete: 'then this page will complete the acceptance.',
    signInGoogle: 'Sign In with Google',
    signedUpAs: 'Signed up as:',
    goToDashboard: 'Go to Dashboard',
    goToLogin: 'Go to Login',
    noToken: 'No invitation token provided. Please check your invite link.',
    networkError: 'Network error. Please try again.',
  },

  // ── Bug Approval Result ──────────────────────────────────────────────────
  approvalResult: {
    approved: 'Bug Approved for Fixing',
    alreadyApproved: 'Already Approved',
    approvalFailed: 'Approval Failed',
    approvedBody: (id: string) => `Bug ${id} has been approved for fixing and is now in the implementation queue.`,
    alreadyBody: (id: string) => `Bug ${id} was already approved. No changes were made.`,
    bugClosed: 'Bug Closed',
    bugClosedBody: (id: string) => `Bug ${id} has been closed. Thanks for confirming the fix.`,
    bugReactivated: 'Bug Reactivated',
    bugReactivatedBody: (id: string) => `Bug ${id} has been reopened and added back to the queue.`,
    pipelineHeld: 'Pipeline Held',
    pipelineHeldBody: (id: string) => `The auto-started fix for bug ${id} has been stopped. Nothing will be changed until you release it on the pipeline board.`,
    wontFix: "Won't Fix Recorded",
    wontFixBody: (id: string) => `Bug ${id} is closed as won't fix, as the pipeline recommended. Nothing will be changed and the reporter has not been emailed. Reopen it from the bug page if you change your mind.`,
    queueAttached: (count: number) => `Added to the work item (${count} report${count === 1 ? '' : 's'})`,
    queueAttachedBody: (count: number) => `${count} late-arrival report${count === 1 ? '' : 's'} ${count === 1 ? 'was' : 'were'} added to the feature-work item its siblings are filed under, and closed as Duplicate Work of it. Each reporter was told once, with a link. Nothing was approved or re-prioritised — the item keeps the decision you already made. Follow the work on the pipeline board.`,
    queueApproved: (count: number) => `Plan approved (${count} bug${count === 1 ? '' : 's'})`,
    queueApprovedBody: (count: number) => `${count} report${count === 1 ? '' : 's'} from this plan ${count === 1 ? 'is' : 'are'} now approved for fixing, in the order you approved. Anything the plan declined, or that was already decided, was left alone. Follow the work on the pipeline board.`,
    viewPipelineBoard: 'Pipeline Board',
    viewBugReport: 'View Bug Report',
    bugRoster: 'Bug Roster',
  },

  // ── Roster Page ──────────────────────────────────────────────────────────
  rosterPage: {
    title: 'Employee Roster',
    employeesShown: (count: number) => `${count} employee${count !== 1 ? 's' : ''} shown`,
    filterAll: 'All',
    filterActive: 'Active',
    filterTerminated: 'Terminated',
    addEmployee: '+ Add Employee',
    headers: { name: 'Name', position: 'Position', email: 'Email', status: 'Status', hireDate: 'Hire Date', daysWorking: 'Days Working', start: 'Start', end: 'End', actions: 'Actions' },
    noEmployees: 'No employees found',
    terminate: 'Terminate',
    addTitle: 'Add New Employee',
    fullName: 'Full Name',
    email: 'Email Address',
    hireDate: 'Hire Date',
    daysWorking: 'Days Working',
    startTime: 'Start Time',
    endTime: 'End Time',
    modifiedSchedule: 'Modified schedule (differs by day)',
    modifiedScheduleHint: 'Only fill the days that differ from the Start/End above — a blank field keeps the default.',
    days: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },
    saveChanges: 'Save Changes',
    editTitle: (name: string) => `Edit: ${name}`,
    terminateTitle: 'Terminate Employee',
    terminateConfirm: (name: string) => `Are you sure you want to terminate ${name}? This will move them to the Terminated list.`,
    terminationDate: 'Termination Date',
    confirmTermination: 'Confirm Termination',
    loadingRoster: 'Loading roster…',
    saving: 'Saving…',
    saveFailed: 'Failed to save. Please try again.',
    loadFailed: 'Failed to load roster',
    nameRequired: 'Name is required',
    positionRequired: 'Position is required',
    // The expandable employee file (Oct 2026): one row opens everything the Employees pages know about
    // a person — skills, trainings, checklists and their grades, onboarding, probation, attendance.
    profile: {
      showDetails: (name: string) => `Show ${name}'s file`,
      hideDetails: (name: string) => `Hide ${name}'s file`,
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      searchPlaceholder: 'Search name, position, skill or checklist…',
      loading: 'Loading training records…',
      unavailable: 'Could not be loaded',
      noAccess: 'You do not have access to this section',
      chipSkills: (n: number) => `${n} skill${n === 1 ? '' : 's'}`,
      chipTraining: (n: number, late: number) => `${n} in training${late > 0 ? ` · ${late} late` : ''}`,
      chipOnboarding: (done: number, total: number) => `Onboarding ${done}/${total}`,
      chipNewHire: (day: number) => `New hire · day ${day}`,
      tenure: (years: number, months: number, days: number) =>
        years > 0 ? `${years} yr${months > 0 ? ` ${months} mo` : ''}`
          : months > 0 ? `${months} mo`
            : `${days} day${days === 1 ? '' : 's'}`,
      hired: 'Hired',
      employedFor: 'Employed',
      leftOn: 'Left',
      schedule: 'Schedule',
      lead: 'Lead status',
      leadYes: 'Lead',
      leadBackup: 'Backup lead',
      leadIc: 'Team member',
      backup: 'Backup',
      backupTrained: 'trained',
      backupNotTrained: 'not trained yet',
      noBackup: 'No backup named',
      recordOnly: 'On the matrix for the record only — never scheduled',
      scoresTitle: 'Training scorecard',
      scoreSkills: 'Skills signed off',
      scoreInTraining: 'In training',
      scoreLate: (n: number) => `${n} late`,
      scoreCompleted: 'Trainings completed',
      scoreEvidenced: (n: number) => `${n} signed off on a checklist`,
      scoreGrades: 'Checklist grades',
      scoreGradesHint: 'Every task graded on an onboarding sheet or a training sign-off',
      scoreOnboarding: 'Onboarding sheets',
      scoreOnboardingValue: (done: number, open: number) => `${done} done · ${open} open`,
      scoreProbation: 'Probation',
      scoreProbationValue: (avg: number) => `${avg.toFixed(1)} / 5`,
      scoreDecision: (n: number) => `decision ${n}/10`,
      scoreReview: '90-day review',
      scoreAttendance: 'Attendance (90 days)',
      scoreAttendanceValue: (n: number) => n === 0 ? 'No incidents' : `${n} incident${n === 1 ? '' : 's'}`,
      none: 'None',
      notScored: 'Not scored yet',
      excels: 'Excels',
      competent: 'Competent',
      needsPractice: 'Needs practice',
      ungraded: 'Not graded',
      skillsTitle: 'Skills',
      skillsSummary: (complete: number, inTraining: number) =>
        `${complete} signed off${inTraining > 0 ? ` · ${inTraining} in training` : ''}`,
      skillInTraining: 'in training',
      skillFailed: 'not passed',
      skillLead: 'lead',
      skillBackupLead: 'backup lead',
      noSkills: 'No skills signed off yet',
      notOnMatrix: 'Not on the Skills Matrix',
      trainingTitle: 'Training',
      inProgress: 'In progress',
      completed: 'Completed',
      noTraining: 'No trainings recorded',
      trainer: 'Trainer',
      started: 'Started',
      due: 'Due',
      done: 'Done',
      daysLate: (n: number) => `${n} day${n === 1 ? '' : 's'} late`,
      viewChecklist: 'View checklist',
      viewSignoff: 'View sign-off',
      noChecklist: 'No checklist linked — signed off with a note',
      noChecklistOpen: 'No checklist linked yet',
      trainingOthers: (n: number) => `Training ${n} other${n === 1 ? '' : 's'} right now`,
      neoTitle: 'Onboarding sheets (NEO)',
      noNeo: 'No onboarding sheets assigned',
      assigned: 'Assigned',
      target: 'Target',
      signedOffBy: 'Signed off by',
      newHireTitle: 'New hire journey',
      dayOf: (n: number) => `Day ${n}`,
      week: (n: number) => `Week ${n}`,
      review90: '90-day review',
      probationNotStarted: 'Probation check-ins not started',
      concerns: (n: number) => `${n} rating${n === 1 ? '' : 's'} of 1–2`,
      finalDecision: 'Hiring decision',
      checkpoint: {
        complete: 'done',
        overdue: 'late',
        'due-soon': 'due soon',
        upcoming: 'upcoming',
        waived: 'waived',
      },
      reviewStatus: {
        scheduled: 'Scheduled',
        completed: 'Completed',
        waived: 'Waived',
      },
      attendanceTitle: 'Attendance & time off',
      lastDays: (n: number) => `Last ${n} days`,
      noShows: 'no-shows',
      tardies: 'tardy',
      earlyLeaves: 'left early',
      noIncidents: 'No incidents in the last 90 days',
      incidentType: {
        'no-show': 'No-show',
        tardy: 'Tardy',
        'early-leave': 'Left early',
      },
      timeOffYear: (year: number) => `Time off approved in ${year}`,
      daysCount: (n: number) => `${n} day${n === 1 ? '' : 's'}`,
      pendingRequests: (n: number) => `${n} request${n === 1 ? '' : 's'} pending`,
      upcomingTimeOff: 'Coming up',
      noUpcoming: 'Nothing coming up',
      timeOffType: {
        vacation: 'Vacation',
        sick: 'Sick',
        personal: 'Personal',
        bereavement: 'Bereavement',
        other: 'Other',
      },
      pending: 'pending',
      openIn: 'Open in',
      links: {
        skillsMatrix: 'Skills Matrix',
        tip: 'Training In Progress',
        neo: 'NEO Training Sheets',
        journey: 'New Hire Journey',
        attendance: 'Attendance Log',
        approvals: 'Time-Off Approvals',
      },
      viewer: {
        close: 'Close',
        kind: {
          'neo-sheet': 'Onboarding sheet',
          'tip-signoff': 'Training sign-off',
          'tip-plan': 'Sign-off checklist — not signed yet',
        },
        status: {
          'in-progress': 'In progress',
          completed: 'Signed off',
          planned: 'Planned',
        },
        planNote: 'Nothing has been ticked yet. These are the tasks the trainer will check when this training is signed off on Training In Progress.',
        tally: (done: number, total: number) => `${done} of ${total} tasks done`,
        task: 'Task',
        doneCol: 'Done',
        grade: 'Grade',
        notes: 'Notes',
        notDone: 'Not done',
        skill: 'Skill',
        department: 'Department',
        trainer: 'Trainer',
        assigned: 'Assigned',
        started: 'Started',
        target: 'Target',
        completed: 'Completed',
        managerSignOff: 'Manager sign-off',
        employeeSignOff: 'Employee sign-off',
        note: 'Note',
        templateState: {
          unsigned: 'Checklist had not been validated when this was signed off',
          signed: 'Checklist was validated when this was signed off',
          stale: 'Checklist had been edited since it was validated',
        },
      },
    },
  },

  // ── GrowFlow Page ────────────────────────────────────────────────────────
  growflowPage: {
    title: 'Product Inventory',
    subtitle: 'Live GrowFlow data — all rooms, all SKUs',
    refreshBtn: 'Refresh from GrowFlow',
    syncing: 'Syncing…',
    lastSynced: 'Last synced',
    noDataYet: 'No data yet — press Refresh to pull from GrowFlow',
    totalSkus: 'Total SKUs',
    totalUnits: 'Total Units',
    rooms: 'Rooms',
    filteredUnits: 'Filtered Units',
    searchPlaceholder: 'Search product name, compliance ID, room…',
    allRooms: 'All Rooms',
    headers: { product: 'Product', qty: 'Qty', room: 'Room', type: 'Type', status: 'Status', batchDate: 'Batch Date', complianceId: 'Compliance ID' },
    noMatch: 'No items match your search',
    noInventory: 'No inventory data yet',
    noInventoryHint: 'Press "Refresh from GrowFlow" above to pull the latest inventory.',
  },

  // ── New Recipe Page ──────────────────────────────────────────────────────
  newRecipePage: {
    title: 'New Recipe Calculator',
    subtitle: 'Full production recipe with cannabinoid dosing — works from scratch or with recycled waste.',
    product: 'Product',
    batchAndWaste: 'Batch & Waste',
    cannabinoids: 'Cannabinoids',
    batchSize: 'New Batch Size (pieces)',
    recycledWaste: 'Recycled Waste (grams)',
    piecesEach: (count: number, weight: number) => `≈ ${count} pieces (${weight}g each)`,
    targetPerPiece: 'Target per Piece (mg)',
    recycledPotency: 'Recycled Potency per Piece (mg)',
    oilPotency: 'Cannabis Oil Potency (%)',
    highRecycled: 'High Recycled Content',
    highRecycledMsg: (pct: string, label: string, max: number) => `Recycled waste is ${pct}% of the ${label}. Above ${max}% may affect texture. Consider splitting across multiple batches.`,
    shellInFilling: (label: string) => `Shell Chocolate in ${label}`,
    shellInFillingMsg: (grams: string, name: string, label: string) => `Recycled mass includes ${grams}g of ${name} from shells. This becomes additional mass in the ${label}.`,
    oilRequired: 'Cannabis Oil Required',
    oilSubtitle: 'Additional oil to reach target potency',
    oilHeaders: { cannabinoid: 'Cannabinoid', totalNeeded: 'Total Needed', fromRecycled: 'From Recycled', additional: 'Additional', oilG: 'Oil (g)' },
    noCannabinoids: 'No cannabinoid targets set. Enter target potency above.',
    totalOil: 'Total Cannabis Oil',
    recycledBreakdown: 'Recycled Waste Breakdown',
    noRecycled: 'No Recycled Waste',
    noRecycledHint: 'Enter recycled waste grams above to see breakdown. With 0g recycled, the full recipe is calculated from scratch.',
    totalRecycled: 'Total Recycled',
    recipeFor: (label: string, name: string) => `${label} Recipe — ${name}`,
    batchOfPieces: (count: string) => `Batch of ${count} pieces`,
    adjustedFor: (grams: string) => `, adjusted for ${grams}g recycled waste`,
    ingredient: 'Ingredient',
    fullRecipe: 'Full Recipe',
    fromRecycledCol: 'From Recycled',
    freshNeeded: 'Fresh Needed',
    amount: 'Amount',
    inStock: 'In Stock',
    shellChocolate: 'Shell Chocolate',
    cannabisOilRow: 'Cannabis Oil',
    totalFresh: 'Total Fresh Ingredients',
    summary: 'Summary',
    summaryLabels: { product: 'Product', batchSize: 'Batch Size', recycled: 'Recycled', totalFilling: 'Total Filling Mass', shellNeeded: 'Shell Chocolate Needed', oilNeeded: 'Total Cannabis Oil', freshIngredients: 'Fresh Ingredients Needed' },
  },

  // ── Access Control Page ──────────────────────────────────────────────────
  accessControlPage: {
    title: 'Access Control',
    subtitle: 'Manage users, invitations, and page-level permissions',
    enforcement: 'Enforcement',
    shadowMode: 'SHADOW (logging only)',
    enforcedMode: 'ENFORCED',
    switchTo: (mode: string) => `Switch to ${mode}`,
    shadow: 'Shadow',
    enforced: 'Enforced',
    tabs: {
      users: (count: number) => `Users (${count})`,
      invites: (count: number) => `Invites (${count})`,
      permissions: 'Permissions Matrix',
      audit: (count: number) => `Audit Log (${count})`,
      devices: 'Devices',
    },
    userRoster: 'User Roster',
    inviteNewUser: '+ Invite New User',
    sendNewInvite: '+ Send New Invite',
    invitations: 'Invitations',
    noInvitations: 'No invitations yet',
    editAccess: 'Edit Access',
    revoke: 'Revoke',
    disable: 'Disable',
    enable: 'Enable',
    youSuperAdmin: 'You (super-admin)',
    thEmail: 'Email',
    thName: 'Name',
    thRole: 'Role',
    thStatus: 'Status',
    thInvitedBy: 'Invited By',
    thAccepted: 'Accepted',
    thActions: 'Actions',
    thAccessTemplate: 'Access Template',
    thSent: 'Sent',
    thExpires: 'Expires',
    routes: (count: number) => `(${count} routes)`,
    custom: (count: number) => `Custom (${count} routes)`,
    none: 'None',
    loadingData: 'Loading access control data...',
    validEmail: 'Enter a valid email address',
    permissionsMatrix: 'Permissions Matrix',
    selectUser: 'Select a user to view their permissions',
    noStaffUsers: 'No staff users yet. Invite someone first.',
    route: 'Route',
    access: 'Access',
    auditLogTitle: 'Audit Log',
    noAuditEvents: 'No audit events recorded yet',
    thTimestamp: 'Timestamp',
    thAction: 'Action',
    thActor: 'Actor',
    thTarget: 'Target',
    thDetails: 'Details',
    inviteModal: {
      title: 'Invite New User',
      step1: '① Choose Access Template',
      step2: '② Customize Email',
      step3: '③ Review & Send',
      emailLabel: 'Email Address',
      emailPlaceholder: 'staff@greenlabsllc.com',
      subjectLabel: 'Email Subject',
      bodyLabel: 'Email Body (HTML)',
      advancedPerms: 'Advanced: Per-Route Overrides',
      sendInvite: 'Send Invite',
      sending: 'Sending…',
    },
    editPermsModal: {
      title: (email: string) => `Edit Permissions — ${email}`,
      savePerms: 'Save Permissions',
    },
  },

  // ── Production Dashboard Page ────────────────────────────────────────────
  productionDashboardPage: {
    title: 'Production Dashboard',
    statuses: {
      scheduled: 'Scheduled',
      inProduction: 'In Production',
      productionComplete: 'Production Complete',
      awaitingPackaging: 'Awaiting Packaging',
      finalPackaging: 'Final Packaging',
      readyToSell: 'Ready to Sell',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    filterAll: 'All',
    noBatches: 'No batches match the current filter.',
    loadingBatches: 'Loading batches…',
    batchId: 'Batch',
    pieces: 'pcs',
    scheduled: 'Scheduled',
    sensorSection: 'Environment Sensors',
    sensorNotConfigured: 'SensorPush not configured',
    temperature: 'Temperature',
    humidity: 'Humidity',
    calibrationTrim: 'trim',
    noSensors: 'No sensors selected',
  },

  // ── Batch Detail Page ────────────────────────────────────────────────────
  batchDetailPage: {
    notFound: 'Batch not found',
    loadingBatch: 'Loading batch…',
    tabs: { overview: 'Overview', production: 'Production', packaging: 'Packaging' },
    editMode: 'Edit',
    saveChanges: 'Save Changes',
    cancelEdit: 'Cancel',
    print: 'Print',
    status: 'Status',
    scheduledAt: 'Scheduled',
    completedAt: 'Completed',
    scheduledManHours: 'Man Hours (est)',
    actualManHours: 'Actual Man Hours',
    scheduledTeam: 'Scheduled Team Size',
    actualTeam: 'Actual Team Size',
    productionNotes: 'Production Notes',
    productionLead: 'Production Lead',
    location: 'Location',
    finalAvgWeight: 'Final Avg Weight',
    validServings: 'Valid Servings',
    totalWaste: 'Total Waste',
    packagingScheduled: 'Packaging Scheduled',
    packagingCompleted: 'Packaging Completed',
    totalSinglePieces: 'Total Single Pieces',
    collectedWaste: 'Collected Waste',
    finalPackaged: 'Final Packaged',
    packManHoursSingle: 'Packaging Hours (Single)',
    packManHoursFinal: 'Packaging Hours (Final)',
    packagingNotes: 'Packaging Notes',
    confirmBack: 'Move batch back to previous status?',
    yes: 'Yes',
    no: 'No',
  },

  // ── Public (marketing) page ───────────────────────────────────────────────
  public: {
    ageGate: {
      question: 'Are you 21 or older?',
      description: 'This site contains cannabis products intended for adults 21 and over in Washington State.',
      yes: "YES, I'M 21+",
      no: 'NO',
      disclaimer: '21+ · Washington State · Not for Kids · Chapter 246-70 WAC',
      mustBe21: 'You must be 21 or older to view this site.',
    },
    hero: {
      line1: 'BOLD FLAVORS.',
      line2: 'REAL VIBES.',
      description: 'Premium cannabis gummies crafted in Washington State. Six bold flavors — 10 pieces per bag, each precisely dosed.',
      cta: 'EXPLORE FLAVORS',
    },
    products: {
      headline: 'SIX FLAVORS. ONE VIBE.',
      subtitle: 'Available at licensed dispensaries across Washington State',
      perBag: 'Per Bag',
      perPiece: 'Per Piece',
    },
    brand: {
      madeIn: 'MADE IN WASHINGTON STATE',
      headline1: 'Crafted with care.',
      headline2: 'Dosed with precision.',
      description:
        'Every Goodiez Gummy is produced by Green Labs LLC under strict Washington State cannabis regulations — lab-tested, WSLCB-compliant, and designed to deliver a consistent, enjoyable experience every time.',
    },
    findUs: {
      headline: 'FIND US IN WASHINGTON STATE',
      description:
        'Goodiez Gummies are available at licensed cannabis retailers across Washington State. Ask your budtender for us by name!',
      contact: 'CONTACT US',
    },
    footer: {
      copyright: '© 2026 Green Labs LLC dba Swifts Edibles · goodiezgummies.com',
      disclaimer:
        'For use only by adults 21 years of age and older. Keep out of reach of children. Washington State licensed cannabis producer/processor. Chapter 246-70 WAC compliant. This product has intoxicating effects and may be habit forming.',
    },
  },
}

// ─── Spanish ──────────────────────────────────────────────────────────────────
const es: typeof en = {
  portalName: 'Portal Goodiez',
  companyName: 'Home',
  signOut: 'Cerrar sesión',
  backToDashboard: '← Panel',
  adminOnly: 'SOLO ADMIN',
  comingSoon: 'PRÓXIMAMENTE',

  dark: 'Oscuro',
  light: 'Claro',

  login: {
    title: 'Portal Goodiez',
    subtitle: 'Home · Solo Personal Autorizado',
    signInGoogle: 'Iniciar sesión con Google',
    restricted: 'Acceso restringido a cuentas @greenlabsllc.com autorizadas.',
  },

  dashboard: {
    welcome: (name: string) => `Bienvenido, ${name} 👋`,
    adminAccess: 'Acceso administrativo completo.',
    staffAccess: 'Acceso de empleado.',
  },

  modules: {
    recipes:    { title: 'Productos',     desc: 'Gestionar productos, recetas y procedimientos' },
    inventory:  { title: 'Inventario',    desc: 'Rastrear ingredientes y niveles de stock' },
    growflow:   { title: 'Inventario de Producto', desc: 'Datos en vivo de GrowFlow — todas las salas y SKUs' },
    production: { title: 'Producción',    desc: 'Seguimiento de lotes y programación' },
    employees:  { title: 'Empleados',     desc: 'Gestión de personal y accesos' },
    settings:    { title: 'Configuración',  desc: 'Configuración del sistema' },
    maintenance: { title: 'Mantenimiento',  desc: 'Estado de equipos y gestión de repuestos' },
    sales:       { title: 'Ventas',         desc: 'Datos trimestrales e inteligencia de ventas' },
    accessControl: { title: 'Control de Acceso', desc: 'Gestionar usuarios, invitaciones y permisos de página' },
  },

  employees: {
    title: 'Empleados',
    subtitle: 'Gestionar registros, habilidades y capacitaciones del personal — solo admin',
    employeeListTab: 'Lista de Empleados',
    timeOffCalendarTab: 'Calendario de Ausencias',
    roster: {
      title: 'Lista de Empleados',
      desc: 'Ver y gestionar todos los empleados — abre a cualquiera para ver sus habilidades, calificaciones y listas de capacitación',
    },
    skillsMatrix: {
      title: 'Matriz de Habilidades',
      desc: 'Rastrear las habilidades de cada empleado, quién lidera cada área y el estado de cobertura de respaldo',
    },
    trainingInProgress: {
      title: 'Capacitación en Curso',
      desc: 'Monitorear registros de capacitación activos, marcar finalizaciones y asignar nuevas habilidades para el desarrollo',
    },
    productionTeams: {
      title: 'Equipos de Producción',
      desc: 'Inteligencia de solo lectura sobre composición del equipo, cobertura, factibilidad de lotes y salud por familia de producto',
    },
    skillEligibility: {
      title: 'Elegibilidad de Habilidades',
      desc: 'Ver las habilidades de creación requeridas y la elegibilidad de empleados por receta',
    },
  },

  recipes: {
    title: 'Productos',
    subtitle: 'Gestionar productos, recetas y procedimientos',
    newRecipe:            { title: 'Nuevo Lote',                    desc: 'Iniciar un nuevo lote de producción — seleccionar SKU, sabor y porciones con dosificación completa de cannabinoides' },
    sops:                 { title: 'Procedimientos',               desc: 'Procedimientos operativos estándar para producción' },
    recipeBooks:          { title: 'Libros de Recetas',            desc: 'Explorar todas las colecciones de recetas guardadas' },
    recipeLeaflinkMapping:{ title: 'Mapeo Receta a LeafLink',     desc: 'Alinear nombres de sabores de recetas con nombres canónicos de productos LeafLink' },
    ingredientMapping:    { title: 'Mapeo de Ingredientes',       desc: 'Resolver nombres de ingredientes de recetas con registros canónicos de inventario' },
    allSku:               { title: 'Gestión de SKU',        desc: 'Gestionar todos los SKUs de productos — crear, editar y configurar artículos de producción', deleteProduct: 'Eliminar Producto', deleteConfirmMessage: '¿Está seguro de que desea eliminar permanentemente este producto?', deleteNote: 'Las recetas, el inventario y los artículos de empaque NO se verán afectados.', cancelDelete: 'Cancelar', confirmDelete: 'Eliminar Permanentemente', deletingProduct: 'Eliminando…' },
  },

  inventory: {
    title: 'Inventario',
    subtitle: 'Rastrear ingredientes, materiales de empaque y niveles de stock de productos terminados',
    productInventory: {
      title: 'Inventario de Productos',
      desc: 'Inventario de bienes terminados rastreado en GrowFlow (sistema de trazabilidad WSLCB)',
    },
    growFlowInventory: {
      title: 'Inventario GrowFlow',
      desc: 'Datos en vivo de GrowFlow — todas las salas, todos los SKUs con resolución de nombres LeafLink',
    },
    productNameMapping: {
      title: 'Mapeo de Nombres de Producto',
      desc: 'Gestionar mapeo de nombres de productos entre las convenciones de GrowFlow y LeafLink',
    },
    growFlowPlayland: {
      title: 'GrowFlow Playland',
      desc: 'Entorno aislado para explorar flujos de creación/destrucción en GrowFlow — restringido a la sala Fabian Test Room',
    },
    expirationTracking: {
      title: 'Seguimiento de Caducidad',
      desc: 'Artículos vencidos o próximos a vencer según la ventana de venta del SKU, con señales de velocidad de ventas por lote',
    },
    packagingIngredients: {
      title: 'Empaque e Ingredientes',
      desc: 'Rastrear materiales de empaque e ingredientes — registro diario de entregas, alertas de reabastecimiento y conteos completos',
    },
    howItWorks: 'Cómo funciona',
    workflowGuide: 'GUÍA DE FLUJO',
    splitExplain:
      'El inventario está dividido en dos sistemas separados porque tienen diferentes propósitos y son gestionados por diferentes herramientas:',
    productInventoryExplain:
      'Gomitas, trufas y comestibles terminados listos para la venta. Rastreados en GrowFlow para cumplimiento de trazabilidad WSLCB. Cualquier movimiento de producto terminado debe pasar por GrowFlow.',
    packagingExplain:
      'Materias primas y suministros de empaque usados en producción. Rastreados aquí en este sistema. Se actualizan diariamente con cada entrega y semanalmente durante los conteos completos.',
    daily: 'DIARIO',
    weekly: 'SEMANAL',
    dailySteps: [
      {
        icon: '🚚',
        title: 'Registrar cada entrega',
        detail: 'Abrir Empaque e Ingredientes → pestaña Registro de Entregas. Ingresar el transportista, número de seguimiento, tipo R/D, contenido (ser específico — ej. "50 lb azúcar, 3 cajas chocolate") y quién firmó. Hacerlo en el momento en que llega la entrega.',
      },
      {
        icon: '✏️',
        title: 'Actualizar cantidades después de cada entrega',
        detail: 'Ir a la pestaña correspondiente (Ingredientes, Empaque, Rollos, etc.). Hacer clic en la celda de la columna Actual para cada artículo recibido — se convertirá en un campo editable. Escribir el nuevo total disponible y presionar Enter para guardar. También actualizar las columnas Recibido y Último Recibido.',
      },
      {
        icon: '🔴',
        title: 'Verificar alertas de reabastecimiento',
        detail: 'En cada pestaña, hacer clic en el botón de filtro rojo "Se necesita reabastecimiento" para ver solo los artículos que requieren pedido. Los artículos con barra de stock roja o Reabast.? = SÍ son prioritarios. Pasar la lista filtrada a compras.',
      },
    ],
    weeklySteps: [
      {
        icon: '🧮',
        title: 'Conteo físico completo de inventario',
        detail: 'Recorrer las instalaciones y contar todo. Actualizar la columna Actual en cada pestaña para reflejar las cantidades reales disponibles. Trabajar en orden: Ingredientes → Empaque → Rollos → Bañados → Lab → General.',
      },
      {
        icon: '📋',
        title: 'Elaborar órdenes de compra desde la lista de reabastecimiento',
        detail: 'Usar el filtro "Se necesita reabastecimiento" en las pestañas de Ingredientes y Empaque. Cruzar referencia con la columna Mín 3 meses (pedir de inmediato si está por debajo) y la columna Suministro 6 meses (buffer ideal). Las pestañas de Empaque y Rollos son impulsadas por pronóstico — los faltantes se calculan automáticamente.',
      },
      {
        icon: '📦',
        title: 'Verificar que el registro de entregas esté completo',
        detail: 'Revisar la pestaña Registro de Entregas y confirmar que todas las entregas de la semana estén registradas. Asegurarse de que las columnas Último Recibido y Recibido en cada pestaña de inventario coincidan con lo que realmente llegó. Las brechas aquí causan errores de conteo en el siguiente conteo completo.',
      },
    ],
  },

  maintenance: {
    title: 'Mantenimiento',
    subtitle: 'Monitoreo del estado de equipos y registros de servicio',
    status: {
      title: 'Mantenimiento Ahora',
      desc: 'Protocolos pendientes, fotos, listas y programacion de hoy',
    },
    equipment: {
      title: 'Equipos',
      desc: 'Administrar máquinas, cronogramas de mantenimiento y registros de servicio',
    },
    machineParts: {
      title: 'Repuestos de Máquinas',
      desc: 'Repuestos y consumibles de cada máquina — existencias, alertas de reorden y números de parte',
    },
  },

  settingsSub: {
    bugs: {
      title: 'Reportes de Errores',
      desc: 'Ver, clasificar y gestionar errores reportados y solicitudes de funciones',
    },
    disasterPlanning: {
      title: 'Respaldos y Restauración',
      desc: 'Respaldos duraderos, simulacros de recuperación y restauración protegida — planificación de desastres Sprint 2 + Sprint 3, todo en un solo lugar',
    },
    trash: {
      title: 'Papelera',
      desc: 'Productos, lotes y libros de recetas en la papelera — restaura en un clic antes de recurrir a un respaldo completo',
    },
  },

  bugReport: {
    modalTitle: 'Reportar un Error',
  },

  sales: {
    title: 'Ventas',
    subtitle: 'Análisis de ventas, rendimiento trimestral e inteligencia de datos',
    quarterlyData: {
      title: 'Datos Trimestrales',
      desc: 'Ver rendimiento de ventas, tendencias de ingresos y comparaciones trimestrales',
    },
    dataIntelligence: {
      title: 'Centro de Inteligencia de Datos',
      desc: 'Perspectivas de mercado, pronósticos y análisis avanzado de ventas',
    },
    returns: {
      title: 'Devoluciones',
      desc: 'Seguimiento de devoluciones de productos, gestión de resoluciones y tendencias',
    },
    complaints: {
      title: 'Quejas de Clientes',
      desc: 'Cada queja como un caso — el lado del cliente y el de la planta, y a quién le toca en cada uno',
      badge: '{n} abiertas',
    },
    payments: {
      title: 'Pagos',
      desc: 'El rastreador de pagos minoristas — la entrega del miércoles, el resultado de cada factura, la lista pendiente',
    },
    nonInfused: {
      title: 'Solicitudes No Infundidas',
      desc: 'Registrar y rastrear solicitudes de muestras no cannábicas por SKU/sabor',
    },
    training: {
      title: 'Capacitación',
      desc: 'Sesiones de capacitación del portal — ver las grabaciones de los recorridos del equipo',
    },
  },

  production: {
    title: 'Producción',
    subtitle: 'Seguimiento de lotes, programación y calendario de producción',
    productionDashboard: {
      title: 'Panel de Producción',
      desc: 'Ver y gestionar lotes de producción, filtros de estado y detalles de lotes',
    },
    productionTv: {
      title: 'TV',
      desc: 'Vista de TV montada en pared para el piso de producción — fuentes grandes, sensores de sala, tarjetas 3D, solo lectura',
    },
    calendar: {
      title: 'Calendario',
      desc: 'Visualizar los lotes programados y completados en un calendario de producción',
    },
    currentSchedule: {
      title: 'Programa de Producción Actual',
      desc: 'Planificar, confirmar y gestionar entradas reales del programa de producción con soporte de simulación y vista de calendario',
    },
    productionTriggers: {
      title: 'Disparadores de Producción',
      desc: 'Gestionar disparadores automáticos y alertas para hitos y umbrales de producción',
    },
    schedulingSettings: {
      title: 'Configuración de Programación',
      desc: 'Configurar reglas de cobertura de líderes y umbrales de programación para equipos de producción y auto-programación',
    },
    planningLab: {
      title: 'Laboratorio de Planificación',
      desc: 'Simular escenarios de equipo, modelar cambios de personal y pronosticar capacidad de producción trimestral',
    },
    productionStats: {
      title: 'Estadísticas de Producción',
      desc: 'Vista de depuración del programador — comparar SKUs por costos de mano de obra, eficiencia y capacidad de producción',
    },
    complaints: {
      title: 'Quejas',
      desc: 'Quejas de clientes en investigación — identificar el lote, investigar, y Blade escribe el resultado',
      badge: '{n} en investigación',
    },
    pdfMerge: {
      title: 'Unir PDFs',
      desc: 'Combina hojas de impresión, COAs o manifiestos en un solo PDF — en tu navegador, nada se sube',
    },
    sanitationLog: {
      title: 'Registro Diario de Sanitización',
      desc: 'Marca y firma la sanitización diaria de la sala \u2014 una semana por hoja, se guarda para los registros y se puede imprimir',
      weekOf: 'Semana del',
      itemEquipment: 'Artículo / Equipo',
      initials: 'Iniciales',
      mgr: 'Ger.',
      comments: 'Comentarios',
      commentPlaceholder: 'Comentario de la semana\u2026',
      notes: 'Notas',
      approvedBy: 'Aprobado por',
      signToday: 'Firmar la columna de hoy',
      print: 'Imprimir',
      previousWeek: 'Semana anterior',
      nextWeek: 'Semana siguiente',
      thisWeek: 'Esta semana',
      thisWeekSoFar: 'Firmado hasta ahora',
      weekComplete: 'Semana completa',
      records: 'Semanas guardadas',
      tapToSign: 'Toca para firmar como quien lo limpió',
      tapToCountersign: 'Toca para refrendar como gerente',
      notYet: 'Ese día aún no ha ocurrido',
      readOnly: 'Puedes leer esta hoja pero no firmarla \u2014 solicita permiso de Edición en Producción en Control de Acceso.',
    },
  },

  shared: {
    cancel: 'Cancelar',
    save: 'Guardar',
    edit: 'Editar',
    loading: 'Cargando…',
    dismiss: 'cerrar',
    backToEmployees: '← Empleados',
    backToRecipes: '← Recetas',
    backToProduction: '← Producción',
    backToDashboardFull: 'Volver al Panel',
    reset: 'Reiniciar',
    noData: '—',
  },

  inviteAccept: {
    internalDataHub: 'Centro de Datos Interno',
    accepting: 'Aceptando tu invitación...',
    needSignIn: 'Necesitas iniciar sesión con Google antes de aceptar esta invitación.',
    inviteFor: 'Esta invitación es para:',
    signInWith: (email: string) => `Inicia sesión con la cuenta de Google de`,
    thenComplete: 'luego esta página completará la aceptación.',
    signInGoogle: 'Iniciar sesión con Google',
    signedUpAs: 'Registrado como:',
    goToDashboard: 'Ir al Panel',
    goToLogin: 'Ir a Inicio de Sesión',
    noToken: 'No se proporcionó un token de invitación. Por favor verifica tu enlace de invitación.',
    networkError: 'Error de red. Por favor intenta de nuevo.',
  },

  approvalResult: {
    approved: 'Error Aprobado para Corrección',
    alreadyApproved: 'Ya Aprobado',
    approvalFailed: 'Aprobación Fallida',
    approvedBody: (id: string) => `El error ${id} ha sido aprobado para corrección y ahora está en la cola de implementación.`,
    alreadyBody: (id: string) => `El error ${id} ya fue aprobado. No se realizaron cambios.`,
    bugClosed: 'Error Cerrado',
    bugClosedBody: (id: string) => `El error ${id} ha sido cerrado. Gracias por confirmar la solución.`,
    bugReactivated: 'Error Reactivado',
    bugReactivatedBody: (id: string) => `El error ${id} ha sido reabierto y agregado nuevamente a la cola.`,
    pipelineHeld: 'Pipeline Detenido',
    pipelineHeldBody: (id: string) => `La corrección automática del error ${id} ha sido detenida. No se cambiará nada hasta que la reanude en el tablero del pipeline.`,
    wontFix: 'Registrado como No Se Corregirá',
    wontFixBody: (id: string) => `El error ${id} se cerró como "no se corregirá", según lo recomendó el pipeline. No se cambiará nada y no se ha enviado correo al reportero. Puede reabrirlo desde la página del error si cambia de opinión.`,
    queueAttached: (count: number) => `Agregado al elemento de trabajo (${count} reporte${count === 1 ? '' : 's'})`,
    queueAttachedBody: (count: number) => `${count} reporte${count === 1 ? '' : 's'} de llegada tardía ${count === 1 ? 'fue agregado' : 'fueron agregados'} al elemento de trabajo donde están sus reportes hermanos, y ${count === 1 ? 'cerrado' : 'cerrados'} como Trabajo Duplicado. Cada reportante fue avisado una vez, con un enlace. No se aprobó ni se cambió la prioridad de nada — el elemento conserva la decisión que usted ya tomó. Siga el trabajo en el tablero del pipeline.`,
    queueApproved: (count: number) => `Plan aprobado (${count} error${count === 1 ? '' : 'es'})`,
    queueApprovedBody: (count: number) => `${count} reporte${count === 1 ? '' : 's'} de este plan ${count === 1 ? 'está aprobado' : 'están aprobados'} para corrección, en el orden que aprobó. Lo que el plan rechazó, o lo que ya estaba decidido, se dejó sin tocar. Siga el trabajo en el tablero del pipeline.`,
    viewPipelineBoard: 'Tablero del Pipeline',
    viewBugReport: 'Ver Reporte de Error',
    bugRoster: 'Lista de Errores',
  },

  rosterPage: {
    title: 'Lista de Empleados',
    employeesShown: (count: number) => `${count} empleado${count !== 1 ? 's' : ''} mostrado${count !== 1 ? 's' : ''}`,
    filterAll: 'Todos',
    filterActive: 'Activos',
    filterTerminated: 'Terminados',
    addEmployee: '+ Agregar Empleado',
    headers: { name: 'Nombre', position: 'Puesto', email: 'Correo', status: 'Estado', hireDate: 'Fecha de Contratación', daysWorking: 'Días Laborales', start: 'Inicio', end: 'Fin', actions: 'Acciones' },
    noEmployees: 'No se encontraron empleados',
    terminate: 'Terminar',
    addTitle: 'Agregar Nuevo Empleado',
    fullName: 'Nombre Completo',
    email: 'Correo Electrónico',
    hireDate: 'Fecha de Contratación',
    daysWorking: 'Días Laborales',
    startTime: 'Hora de Inicio',
    endTime: 'Hora de Salida',
    modifiedSchedule: 'Horario modificado (varía por día)',
    modifiedScheduleHint: 'Llena solo los días que difieren del Inicio/Salida de arriba — un campo vacío mantiene el horario normal.',
    days: { mon: 'Lun', tue: 'Mar', wed: 'Mié', thu: 'Jue', fri: 'Vie', sat: 'Sáb', sun: 'Dom' },
    saveChanges: 'Guardar Cambios',
    editTitle: (name: string) => `Editar: ${name}`,
    terminateTitle: 'Terminar Empleado',
    terminateConfirm: (name: string) => `¿Estás seguro de que deseas terminar a ${name}? Esto lo moverá a la lista de Terminados.`,
    terminationDate: 'Fecha de Terminación',
    confirmTermination: 'Confirmar Terminación',
    loadingRoster: 'Cargando lista…',
    saving: 'Guardando…',
    saveFailed: 'Error al guardar. Por favor intenta de nuevo.',
    loadFailed: 'Error al cargar la lista',
    nameRequired: 'El nombre es requerido',
    positionRequired: 'El puesto es requerido',
    profile: {
      showDetails: (name: string) => `Ver el expediente de ${name}`,
      hideDetails: (name: string) => `Ocultar el expediente de ${name}`,
      expandAll: 'Expandir todo',
      collapseAll: 'Contraer todo',
      searchPlaceholder: 'Buscar nombre, puesto, habilidad o lista…',
      loading: 'Cargando registros de capacitación…',
      unavailable: 'No se pudo cargar',
      noAccess: 'No tienes acceso a esta sección',
      chipSkills: (n: number) => `${n} habilidad${n === 1 ? '' : 'es'}`,
      chipTraining: (n: number, late: number) => `${n} en capacitación${late > 0 ? ` · ${late} atrasada${late === 1 ? '' : 's'}` : ''}`,
      chipOnboarding: (done: number, total: number) => `Inducción ${done}/${total}`,
      chipNewHire: (day: number) => `Nuevo ingreso · día ${day}`,
      tenure: (years: number, months: number, days: number) =>
        years > 0 ? `${years} año${years === 1 ? '' : 's'}${months > 0 ? ` ${months} mes${months === 1 ? '' : 'es'}` : ''}`
          : months > 0 ? `${months} mes${months === 1 ? '' : 'es'}`
            : `${days} día${days === 1 ? '' : 's'}`,
      hired: 'Contratado',
      employedFor: 'Antigüedad',
      leftOn: 'Salida',
      schedule: 'Horario',
      lead: 'Rol de líder',
      leadYes: 'Líder',
      leadBackup: 'Líder suplente',
      leadIc: 'Miembro del equipo',
      backup: 'Suplente',
      backupTrained: 'capacitado',
      backupNotTrained: 'aún sin capacitar',
      noBackup: 'Sin suplente asignado',
      recordOnly: 'En la matriz solo como registro — nunca se programa',
      scoresTitle: 'Resumen de capacitación',
      scoreSkills: 'Habilidades aprobadas',
      scoreInTraining: 'En capacitación',
      scoreLate: (n: number) => `${n} atrasada${n === 1 ? '' : 's'}`,
      scoreCompleted: 'Capacitaciones completadas',
      scoreEvidenced: (n: number) => `${n} firmada${n === 1 ? '' : 's'} con lista`,
      scoreGrades: 'Calificaciones de listas',
      scoreGradesHint: 'Cada tarea calificada en una hoja de inducción o en una firma de capacitación',
      scoreOnboarding: 'Hojas de inducción',
      scoreOnboardingValue: (done: number, open: number) => `${done} terminada${done === 1 ? '' : 's'} · ${open} abierta${open === 1 ? '' : 's'}`,
      scoreProbation: 'Periodo de prueba',
      scoreProbationValue: (avg: number) => `${avg.toFixed(1)} / 5`,
      scoreDecision: (n: number) => `decisión ${n}/10`,
      scoreReview: 'Revisión de 90 días',
      scoreAttendance: 'Asistencia (90 días)',
      scoreAttendanceValue: (n: number) => n === 0 ? 'Sin incidentes' : `${n} incidente${n === 1 ? '' : 's'}`,
      none: 'Ninguno',
      notScored: 'Sin calificar aún',
      excels: 'Sobresale',
      competent: 'Competente',
      needsPractice: 'Necesita práctica',
      ungraded: 'Sin calificar',
      skillsTitle: 'Habilidades',
      skillsSummary: (complete: number, inTraining: number) =>
        `${complete} aprobada${complete === 1 ? '' : 's'}${inTraining > 0 ? ` · ${inTraining} en capacitación` : ''}`,
      skillInTraining: 'en capacitación',
      skillFailed: 'no aprobada',
      skillLead: 'líder',
      skillBackupLead: 'líder suplente',
      noSkills: 'Aún no tiene habilidades aprobadas',
      notOnMatrix: 'No está en la Matriz de Habilidades',
      trainingTitle: 'Capacitación',
      inProgress: 'En curso',
      completed: 'Completadas',
      noTraining: 'No hay capacitaciones registradas',
      trainer: 'Capacitador',
      started: 'Inicio',
      due: 'Vence',
      done: 'Terminada',
      daysLate: (n: number) => `${n} día${n === 1 ? '' : 's'} de atraso`,
      viewChecklist: 'Ver lista',
      viewSignoff: 'Ver firma',
      noChecklist: 'Sin lista vinculada — se firmó con una nota',
      noChecklistOpen: 'Aún sin lista vinculada',
      trainingOthers: (n: number) => `Capacitando a ${n} persona${n === 1 ? '' : 's'} ahora`,
      neoTitle: 'Hojas de inducción (NEO)',
      noNeo: 'No tiene hojas de inducción asignadas',
      assigned: 'Asignada',
      target: 'Meta',
      signedOffBy: 'Firmada por',
      newHireTitle: 'Trayectoria de nuevo ingreso',
      dayOf: (n: number) => `Día ${n}`,
      week: (n: number) => `Semana ${n}`,
      review90: 'Revisión de 90 días',
      probationNotStarted: 'Revisiones de prueba sin iniciar',
      concerns: (n: number) => `${n} calificación${n === 1 ? '' : 'es'} de 1–2`,
      finalDecision: 'Decisión de contratación',
      checkpoint: {
        complete: 'hecha',
        overdue: 'atrasada',
        'due-soon': 'vence pronto',
        upcoming: 'próxima',
        waived: 'exenta',
      },
      reviewStatus: {
        scheduled: 'Programada',
        completed: 'Completada',
        waived: 'Exenta',
      },
      attendanceTitle: 'Asistencia y tiempo libre',
      lastDays: (n: number) => `Últimos ${n} días`,
      noShows: 'faltas',
      tardies: 'tardanzas',
      earlyLeaves: 'salidas temprano',
      noIncidents: 'Sin incidentes en los últimos 90 días',
      incidentType: {
        'no-show': 'Falta',
        tardy: 'Tardanza',
        'early-leave': 'Salida temprano',
      },
      timeOffYear: (year: number) => `Tiempo libre aprobado en ${year}`,
      daysCount: (n: number) => `${n} día${n === 1 ? '' : 's'}`,
      pendingRequests: (n: number) => `${n} solicitud${n === 1 ? '' : 'es'} pendiente${n === 1 ? '' : 's'}`,
      upcomingTimeOff: 'Próximamente',
      noUpcoming: 'Nada próximo',
      timeOffType: {
        vacation: 'Vacaciones',
        sick: 'Enfermedad',
        personal: 'Personal',
        bereavement: 'Duelo',
        other: 'Otro',
      },
      pending: 'pendiente',
      openIn: 'Abrir en',
      links: {
        skillsMatrix: 'Matriz de Habilidades',
        tip: 'Capacitación en Curso',
        neo: 'Hojas de Inducción NEO',
        journey: 'Trayectoria de Nuevo Ingreso',
        attendance: 'Registro de Asistencia',
        approvals: 'Aprobaciones de Tiempo Libre',
      },
      viewer: {
        close: 'Cerrar',
        kind: {
          'neo-sheet': 'Hoja de inducción',
          'tip-signoff': 'Firma de capacitación',
          'tip-plan': 'Lista para la firma — aún sin firmar',
        },
        status: {
          'in-progress': 'En curso',
          completed: 'Firmada',
          planned: 'Planificada',
        },
        planNote: 'Aún no se ha marcado nada. Estas son las tareas que el capacitador revisará cuando se firme esta capacitación en Capacitación en Curso.',
        tally: (done: number, total: number) => `${done} de ${total} tareas hechas`,
        task: 'Tarea',
        doneCol: 'Hecha',
        grade: 'Calificación',
        notes: 'Notas',
        notDone: 'Sin hacer',
        skill: 'Habilidad',
        department: 'Departamento',
        trainer: 'Capacitador',
        assigned: 'Asignada',
        started: 'Inicio',
        target: 'Meta',
        completed: 'Completada',
        managerSignOff: 'Firma del gerente',
        employeeSignOff: 'Firma del empleado',
        note: 'Nota',
        templateState: {
          unsigned: 'La lista no estaba validada cuando se firmó',
          signed: 'La lista estaba validada cuando se firmó',
          stale: 'La lista se había editado después de validarse',
        },
      },
    },
  },

  growflowPage: {
    title: 'Inventario de Producto',
    subtitle: 'Datos en vivo de GrowFlow — todas las salas, todos los SKUs',
    refreshBtn: 'Actualizar desde GrowFlow',
    syncing: 'Sincronizando…',
    lastSynced: 'Última sincronización',
    noDataYet: 'Sin datos aún — presiona Actualizar para obtener datos de GrowFlow',
    totalSkus: 'SKUs Totales',
    totalUnits: 'Unidades Totales',
    rooms: 'Salas',
    filteredUnits: 'Unidades Filtradas',
    searchPlaceholder: 'Buscar nombre de producto, ID de cumplimiento, sala…',
    allRooms: 'Todas las Salas',
    headers: { product: 'Producto', qty: 'Cant', room: 'Sala', type: 'Tipo', status: 'Estado', batchDate: 'Fecha de Lote', complianceId: 'ID de Cumplimiento' },
    noMatch: 'No hay artículos que coincidan con tu búsqueda',
    noInventory: 'Sin datos de inventario aún',
    noInventoryHint: 'Presiona "Actualizar desde GrowFlow" arriba para obtener el inventario más reciente.',
  },

  newRecipePage: {
    title: 'Calculadora de Nueva Receta',
    subtitle: 'Receta de producción completa con dosificación de cannabinoides — funciona desde cero o con material reciclado.',
    product: 'Producto',
    batchAndWaste: 'Lote y Residuos',
    cannabinoids: 'Cannabinoides',
    batchSize: 'Tamaño de Nuevo Lote (piezas)',
    recycledWaste: 'Material Reciclado (gramos)',
    piecesEach: (count: number, weight: number) => `≈ ${count} piezas (${weight}g c/u)`,
    targetPerPiece: 'Objetivo por Pieza (mg)',
    recycledPotency: 'Potencia Reciclada por Pieza (mg)',
    oilPotency: 'Potencia del Aceite de Cannabis (%)',
    highRecycled: 'Alto Contenido Reciclado',
    highRecycledMsg: (pct: string, label: string, max: number) => `El material reciclado es ${pct}% del ${label}. Más del ${max}% puede afectar la textura. Considera dividir en múltiples lotes.`,
    shellInFilling: (label: string) => `Chocolate de Cubierta en ${label}`,
    shellInFillingMsg: (grams: string, name: string, label: string) => `La masa reciclada incluye ${grams}g de ${name} de cubiertas. Esto se convierte en masa adicional en el ${label}.`,
    oilRequired: 'Aceite de Cannabis Requerido',
    oilSubtitle: 'Aceite adicional para alcanzar la potencia objetivo',
    oilHeaders: { cannabinoid: 'Cannabinoide', totalNeeded: 'Total Necesario', fromRecycled: 'De Reciclado', additional: 'Adicional', oilG: 'Aceite (g)' },
    noCannabinoids: 'No se establecieron objetivos de cannabinoides. Ingresa la potencia objetivo arriba.',
    totalOil: 'Total Aceite de Cannabis',
    recycledBreakdown: 'Desglose de Material Reciclado',
    noRecycled: 'Sin Material Reciclado',
    noRecycledHint: 'Ingresa gramos de material reciclado arriba para ver el desglose. Con 0g reciclado, la receta se calcula desde cero.',
    totalRecycled: 'Total Reciclado',
    recipeFor: (label: string, name: string) => `Receta de ${label} — ${name}`,
    batchOfPieces: (count: string) => `Lote de ${count} piezas`,
    adjustedFor: (grams: string) => `, ajustado por ${grams}g de material reciclado`,
    ingredient: 'Ingrediente',
    fullRecipe: 'Receta Completa',
    fromRecycledCol: 'De Reciclado',
    freshNeeded: 'Fresco Necesario',
    amount: 'Cantidad',
    inStock: 'En Stock',
    shellChocolate: 'Chocolate de Cubierta',
    cannabisOilRow: 'Aceite de Cannabis',
    totalFresh: 'Total Ingredientes Frescos',
    summary: 'Resumen',
    summaryLabels: { product: 'Producto', batchSize: 'Tamaño de Lote', recycled: 'Reciclado', totalFilling: 'Masa Total de Relleno', shellNeeded: 'Chocolate de Cubierta Necesario', oilNeeded: 'Total Aceite de Cannabis', freshIngredients: 'Ingredientes Frescos Necesarios' },
  },

  accessControlPage: {
    title: 'Control de Acceso',
    subtitle: 'Gestionar usuarios, invitaciones y permisos a nivel de página',
    enforcement: 'Aplicación',
    shadowMode: 'SOMBRA (solo registro)',
    enforcedMode: 'APLICADO',
    switchTo: (mode: string) => `Cambiar a ${mode}`,
    shadow: 'Sombra',
    enforced: 'Aplicado',
    tabs: {
      users: (count: number) => `Usuarios (${count})`,
      invites: (count: number) => `Invitaciones (${count})`,
      permissions: 'Matriz de Permisos',
      audit: (count: number) => `Registro de Auditoría (${count})`,
      devices: 'Dispositivos',
    },
    userRoster: 'Lista de Usuarios',
    inviteNewUser: '+ Invitar Nuevo Usuario',
    sendNewInvite: '+ Enviar Nueva Invitación',
    invitations: 'Invitaciones',
    noInvitations: 'No hay invitaciones aún',
    editAccess: 'Editar Acceso',
    revoke: 'Revocar',
    disable: 'Deshabilitar',
    enable: 'Habilitar',
    youSuperAdmin: 'Tú (super-admin)',
    thEmail: 'Email',
    thName: 'Nombre',
    thRole: 'Rol',
    thStatus: 'Estado',
    thInvitedBy: 'Invitado Por',
    thAccepted: 'Aceptado',
    thActions: 'Acciones',
    thAccessTemplate: 'Plantilla de Acceso',
    thSent: 'Enviado',
    thExpires: 'Expira',
    routes: (count: number) => `(${count} rutas)`,
    custom: (count: number) => `Personalizado (${count} rutas)`,
    none: 'Ninguno',
    loadingData: 'Cargando datos de control de acceso...',
    validEmail: 'Ingresa una dirección de email válida',
    permissionsMatrix: 'Matriz de Permisos',
    selectUser: 'Selecciona un usuario para ver sus permisos',
    noStaffUsers: 'No hay usuarios de personal aún. Invita a alguien primero.',
    route: 'Ruta',
    access: 'Acceso',
    auditLogTitle: 'Registro de Auditoría',
    noAuditEvents: 'No hay eventos de auditoría registrados aún',
    thTimestamp: 'Fecha/Hora',
    thAction: 'Acción',
    thActor: 'Actor',
    thTarget: 'Objetivo',
    thDetails: 'Detalles',
    inviteModal: {
      title: 'Invitar Nuevo Usuario',
      step1: '① Elegir Plantilla de Acceso',
      step2: '② Personalizar Email',
      step3: '③ Revisar y Enviar',
      emailLabel: 'Dirección de Email',
      emailPlaceholder: 'staff@greenlabsllc.com',
      subjectLabel: 'Asunto del Email',
      bodyLabel: 'Cuerpo del Email (HTML)',
      advancedPerms: 'Avanzado: Ajustes por Ruta',
      sendInvite: 'Enviar Invitación',
      sending: 'Enviando…',
    },
    editPermsModal: {
      title: (email: string) => `Editar Permisos — ${email}`,
      savePerms: 'Guardar Permisos',
    },
  },

  productionDashboardPage: {
    title: 'Panel de Producción',
    statuses: {
      scheduled: 'Programado',
      inProduction: 'En Producción',
      productionComplete: 'Producción Completa',
      awaitingPackaging: 'Esperando Empaque',
      finalPackaging: 'Empaque Final',
      readyToSell: 'Listo para Venta',
      inProgress: 'En Progreso',
      completed: 'Completado',
      cancelled: 'Cancelado',
    },
    filterAll: 'Todos',
    noBatches: 'No hay lotes que coincidan con el filtro actual.',
    loadingBatches: 'Cargando lotes…',
    batchId: 'Lote',
    pieces: 'pzs',
    scheduled: 'Programado',
    sensorSection: 'Sensores Ambientales',
    sensorNotConfigured: 'SensorPush no configurado',
    temperature: 'Temperatura',
    humidity: 'Humedad',
    calibrationTrim: 'ajuste',
    noSensors: 'No hay sensores seleccionados',
  },

  batchDetailPage: {
    notFound: 'Lote no encontrado',
    loadingBatch: 'Cargando lote…',
    tabs: { overview: 'Resumen', production: 'Producción', packaging: 'Empaque' },
    editMode: 'Editar',
    saveChanges: 'Guardar Cambios',
    cancelEdit: 'Cancelar',
    print: 'Imprimir',
    status: 'Estado',
    scheduledAt: 'Programado',
    completedAt: 'Completado',
    scheduledManHours: 'Horas Hombre (est)',
    actualManHours: 'Horas Hombre Reales',
    scheduledTeam: 'Tamaño de Equipo Programado',
    actualTeam: 'Tamaño de Equipo Real',
    productionNotes: 'Notas de Producción',
    productionLead: 'Líder de Producción',
    location: 'Ubicación',
    finalAvgWeight: 'Peso Promedio Final',
    validServings: 'Porciones Válidas',
    totalWaste: 'Desperdicio Total',
    packagingScheduled: 'Empaque Programado',
    packagingCompleted: 'Empaque Completado',
    totalSinglePieces: 'Total Piezas Individuales',
    collectedWaste: 'Desperdicio Recolectado',
    finalPackaged: 'Empaque Final',
    packManHoursSingle: 'Horas Empaque (Individual)',
    packManHoursFinal: 'Horas Empaque (Final)',
    packagingNotes: 'Notas de Empaque',
    confirmBack: '¿Mover lote al estado anterior?',
    yes: 'Sí',
    no: 'No',
  },

  public: {
    ageGate: {
      question: '¿Tienes 21 años o más?',
      description: 'Este sitio contiene productos de cannabis destinados a adultos de 21 años o más en el Estado de Washington.',
      yes: 'SÍ, TENGO 21+',
      no: 'NO',
      disclaimer: '21+ · Estado de Washington · No para niños · Chapter 246-70 WAC',
      mustBe21: 'Debes tener 21 años o más para ver este sitio.',
    },
    hero: {
      line1: 'SABORES AUDACES.',
      line2: 'VIBRAS REALES.',
      description: 'Gomitas de cannabis premium elaboradas en el Estado de Washington. Seis sabores intensos — 10 piezas por bolsa, cada una con dosis precisa.',
      cta: 'EXPLORAR SABORES',
    },
    products: {
      headline: 'SEIS SABORES. UNA VIBRA.',
      subtitle: 'Disponible en dispensarios autorizados en el Estado de Washington',
      perBag: 'Por Bolsa',
      perPiece: 'Por Pieza',
    },
    brand: {
      madeIn: 'ELABORADO EN EL ESTADO DE WASHINGTON',
      headline1: 'Elaborado con cuidado.',
      headline2: 'Dosificado con precisión.',
      description:
        'Cada Gomita Goodiez es producida por Green Labs LLC bajo estrictas regulaciones de cannabis del Estado de Washington — probada en laboratorio, cumple con WSLCB, y diseñada para brindar una experiencia consistente y placentera cada vez.',
    },
    findUs: {
      headline: 'ENCUÉNTRANOS EN EL ESTADO DE WASHINGTON',
      description:
        'Las Gomitas Goodiez están disponibles en minoristas autorizados de cannabis en todo el Estado de Washington. ¡Pide por nuestro nombre a tu vendedor!',
      contact: 'CONTÁCTANOS',
    },
    footer: {
      copyright: '© 2026 Green Labs LLC dba Swifts Edibles · goodiezgummies.com',
      disclaimer:
        'Solo para uso de adultos de 21 años o más. Mantener fuera del alcance de los niños. Productor/procesador de cannabis con licencia en el Estado de Washington. Cumple con el Capítulo 246-70 WAC. Este producto tiene efectos intoxicantes y puede crear hábito.',
    },
  },
}

// ─── Exports ──────────────────────────────────────────────────────────────────
export const translations: Record<Language, typeof en> = { en, es }
export type Translations = typeof en
